import { useInfiniteQuery } from '@tanstack/react-query';
import { Plus, MessageSquare, RefreshCw, Search, Settings } from 'lucide-react';
import { useCallback, useEffect, useState, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import useInfiniteScroll from 'react-infinite-scroll-hook';
import type { ImperativePanelHandle } from 'react-resizable-panels';
import { Outlet, useNavigate, useLocation, useSearchParams, useParams } from 'react-router-dom';
import { MobileInboxLayout } from './components/MobileInboxLayout';
import i18n from '@/Config/app/i18n';
import { InboxTypeEnum, IndexPath } from '@/Config/app/router/index.path';
import { TRIAGE_DATA_EXPORT_ALLOWED_SCENARIO_IDS } from '@/Config/scenarioIds';
import { ExportMode } from '@/Domain/api/Export/export.dto';
import { ConversationTypeEnum } from '@/Domain/components/App/Sidebar/components/SidebarInboxButton';
import {
  ExportAnalyticsDialog,
  exportAnalyticsDialogName,
} from '@/Domain/components/ExportAnalytics.dialog';
import {
  AUTO_CLOSE_SETTINGS_DIALOG,
  AutoCloseSettingsDialog,
} from '@/Domain/components/Inbox/AutoCloseSettings/AutoCloseSettings.dialog';
import { BulkWhatsAppTemplateDialog } from '@/Domain/components/Inbox/BulkWhatsAppTemplate/BulkWhatsAppTemplateDialog';
import {
  CONVERSATION_BADGE_SETTINGS_DIALOG,
  ConversationBadgeSettingsDialog,
} from '@/Domain/components/Inbox/ConversationBadgeSettings/ConversationBadgeSettings.dialog';
import { ConversationDetails } from '@/Domain/components/Inbox/ConversationDetails/ConversationDetails';
import { ConversationList } from '@/Domain/components/Inbox/ConversationList/ConversationList';
import { ConversationSearch } from '@/Domain/components/Inbox/ConversationSearch/ConversationSearch';
import { TranslationDropdown } from '@/Domain/components/Inbox/TransaltionDropdown/TranslationDropdown';
import { VoiceAlertsDropdown } from '@/Domain/components/Inbox/VoiceAlertsDropdown/VoiceAlertsDropdown';
import { useTrackPageVisit } from '@/Domain/components/OnboardingProgress';
import { Badge } from '@/Domain/components/Shared/Badge/Badge';
import {
  LinkedinIcon,
  TelegramIcon,
  WhatsappIcon,
} from '@/Domain/components/Shared/BrandIcon/BrandIcon';
import { Button } from '@/Domain/components/Shared/Button/Button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/Domain/components/Shared/DropdownMenu/DropdownMenu';
import { ErrorFallback } from '@/Domain/components/Shared/ErrorFallback';
import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/Domain/components/Shared/Resizable/Resizable';
import { Spinner } from '@/Domain/components/Shared/Spinner';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import { UpgradeBadge } from '@/Domain/components/Shared/UpgradeBadge/UpgradeBadge';
import { useSubscription } from '@/Domain/components/SubscriptionProvider';
import {
  typeToFilters,
  useConversationList,
} from '@/Domain/hooks/Conversation/useConversationList.hook';
import { useConversationListAnchor } from '@/Domain/hooks/Conversation/useConversationListAnchor.hook';
import { useConversationsAboveAnchor } from '@/Domain/hooks/Conversation/useConversationsAboveAnchor.hook';
import { useWhatsAppSetupStatus } from '@/Domain/hooks/Integration/useWhatsAppSetupStatus.hook';
import { useUser } from '@/Domain/hooks/User/useUser.hook';
import { useConversationSocket } from '@/Domain/socket/Conversation/useConversation.socket';
import { useConversationStore } from '@/Domain/store/conversation.store';
import { useModalStore } from '@/Domain/store/modal.store';
import { useIsMobile } from '@/Domain/utils/isMobile';
import { SCENARIO_ID } from '@/context';

type PanelSizesType = {
  minSideSize: number;
  defaultSize: number;
  collapsedSize: number;
};

const PANEL_SIZES = {
  DEFAULT: {
    minSideSize: 22,
    defaultSize: 26,
    collapsedSize: 4,
  },
  HIGH_RESOLUTION: {
    minSideSize: 15,
    defaultSize: 20,
    collapsedSize: 2.5,
  },
};

export function inboxTypeToActiveTabTitle(inboxType: InboxTypeEnum | null) {
  switch (inboxType) {
    case null:
      return i18n.t('inbox_v2:tabs.all');
    case InboxTypeEnum.OpenAll:
      return i18n.t('inbox_v2:tabs.open_all');
    // OpenByOther is a legacy alias for the All-assigned view (its filter and data are All), so it shares the Assigned heading.
    case InboxTypeEnum.OpenAllAssigned:
    case InboxTypeEnum.OpenByOther:
      return i18n.t('sidebar_menu:menu.conversations.assigned');
    case InboxTypeEnum.OpenByAI:
      return i18n.t('inbox_v2:tabs.open_ai');
    case InboxTypeEnum.OpenUnassigned:
      return i18n.t('inbox_v2:tabs.open_unassigned');
    case InboxTypeEnum.OpenByMe:
      return i18n.t('inbox_v2:tabs.open_my');
    case InboxTypeEnum.AssumedResolvedByAI:
      return i18n.t('inbox_v2:tabs.resolved_by_ai');
    case InboxTypeEnum.ResolvedByHuman:
      return i18n.t('inbox_v2:tabs.resolved_by_human');
    default:
      return '';
  }
}

const InboxPageContent = () => {
  const { t } = useTranslation('inbox_v2');
  useTrackPageVisit('inbox');
  const [showSearch, setShowSearch] = useState(false);
  const [isConversationPanelActive, setIsConversationPanelActive] = useState(false);
  const [query, setQuery] = useState('');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showBulkWhatsApp, setShowBulkWhatsApp] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { user } = useUser();
  const { data: whatsappStatus } = useWhatsAppSetupStatus();
  // Fail open while status is loading so connected accounts don't flash a disabled bulk-send on cold load.
  const isWhatsAppUnavailable = whatsappStatus != null && whatsappStatus.status !== 'connected';
  // Gate on the `:id` route param, not an absolute path: the `/i` app mount (main.router.tsx) breaks hardcoded `useMatch` patterns.
  const { id: conversationId } = useParams();

  // The three inputs that fully determine the list filters; changing any of them drops the anchor.
  const filtersKey = `${searchParams.get('type')}|${searchParams.get('filter_user_id')}|${query}`;
  const {
    anchorId,
    anchorLastMessageAt,
    anchorConversation,
    isAnchorResolved,
    clearAnchor,
    refetchAnchor,
  } = useConversationListAnchor(conversationId, filtersKey);
  const { conversationsQuery, typeFilters, baseFilters } = useConversationList(
    query,
    anchorLastMessageAt,
  );
  const { aboveConversations, hasMoreAbove, isAboveFetched, refetchAbove } =
    useConversationsAboveAnchor(baseFilters, anchorLastMessageAt, anchorId);
  const { setMessage } = useConversationStore();

  const {
    data: conversations,
    isLoading,
    isFetched,
    fetchNextPage,
    hasNextPage = true,
    isFetchingNextPage: loading,
    isError,
    isRefetching,
    refetch,
    error,
  } = useInfiniteQuery({ ...conversationsQuery, enabled: isAnchorResolved });

  const { toggleModal } = useModalStore();
  const { isActivated, isFetched: isSubscriptionFetched } = useSubscription();
  const canExportDataGathering = !isSubscriptionFetched || isActivated('lead_generation');
  const canExportConversations = !isSubscriptionFetched || isActivated('export_conversations');
  const location = useLocation();
  const locationSearch = location.search;

  const [panelSizes, setPanelSizes] = useState<PanelSizesType>(PANEL_SIZES.DEFAULT);

  const [isRightSidebarCollapsed, setIsRightSidebarCollapsed] = useState(false);

  const rightPanelRef = useRef<ImperativePanelHandle>(null);

  const isMobile = useIsMobile();

  useEffect(() => {
    const mediaQuery = window.matchMedia('(min-width: 2260px)');

    const handleResize = () => {
      setPanelSizes(mediaQuery.matches ? PANEL_SIZES.HIGH_RESOLUTION : PANEL_SIZES.DEFAULT);
    };

    handleResize();
    mediaQuery.addEventListener('change', handleResize);

    return () => mediaQuery.removeEventListener('change', handleResize);
  }, []);

  const onLoadMore = useCallback(() => {
    if (loading) return;
    fetchNextPage().catch(() => null);
  }, [loading]);

  const [sentryRef] = useInfiniteScroll({
    loading,
    hasNextPage,
    onLoadMore,
    // A failed search waits for Refresh or Enter, and for that run to finish: loading it mid-run cancels the run.
    disabled: Boolean(query) && (isError || isRefetching),
  });

  // An anchored list is two queries plus the pinned anchor row, which ConversationList takes from
  // the detail lookup once the anchor has left both windows. Refreshing only the anchor-and-older
  // half leaves the rest rendering stale unread badges, assignees and resolution states.
  const inFlightRefreshRef = useRef<Promise<unknown> | null>(null);
  const trailingRefreshRef = useRef(false);
  const refetchConversations = useCallback(async () => {
    // A busy scenario emits an inbox event per message, so an unguarded handler would start a
    // bundle per event. At most two are ever outstanding: the one in flight, and one trailing
    // run that folds every event arriving while it is, so the list still settles on the latest.
    if (inFlightRefreshRef.current) {
      trailingRefreshRef.current = true;
      return await inFlightRefreshRef.current;
    }
    do {
      trailingRefreshRef.current = false;
      inFlightRefreshRef.current = Promise.all([refetch(), refetchAbove(), refetchAnchor()]);
      try {
        await inFlightRefreshRef.current;
      } finally {
        inFlightRefreshRef.current = null;
      }
    } while (trailingRefreshRef.current);
  }, [refetch, refetchAbove, refetchAnchor]);

  useConversationSocket({ refetch: refetchConversations, query });

  const [inboxTabTitle, setInboxTabTitle] = useState<ConversationTypeEnum>(
    inboxTypeToActiveTabTitle(searchParams.get('type') as InboxTypeEnum | null),
  );
  useEffect(() => {
    setInboxTabTitle(inboxTypeToActiveTabTitle(searchParams.get('type') as InboxTypeEnum | null));
  }, [inboxTypeToActiveTabTitle, searchParams]);

  useEffect(() => {
    // Skip auto-redirect on mobile so users land on the conversation list
    // instead of being pushed straight into the first conversation. The mobile
    // layout is single-pane; auto-redirect would hide the list entirely.
    if (!isFetched || isMobile) return;
    if (conversations) {
      const firstConversation = conversations.pages[0]?.items?.[0]?.id;
      if (!firstConversation) return;
      // An anchored list is a historical window, and the anchor outlives the route param by a
      // render, so without this the first row of a two-week-old window gets auto-selected.
      if (conversationId || anchorId != null) return;

      const params = new URLSearchParams(location.search);
      const inboxType = params.get('type') as InboxTypeEnum | null;

      if (JSON.stringify(typeToFilters(inboxType, user)) !== JSON.stringify(typeFilters)) return;
      navigate(`${firstConversation + locationSearch}`);
    }
  }, [
    isFetched,
    conversations,
    locationSearch,
    conversationId,
    anchorId,
    user,
    navigate,
    isMobile,
  ]);

  const handleToggleRightPanel = (collapse: boolean) => {
    const panel = rightPanelRef.current;
    if (panel) {
      if (collapse) {
        setMessage(null);
        panel.collapse();
      } else {
        panel.expand();
      }
      setIsRightSidebarCollapsed(collapse);
    }
  };

  useEffect(() => {
    if (isLoading) {
      setIsRefreshing(true);
      refetch();
      setTimeout(() => {
        setIsRefreshing(false);
      }, 500);
    }
  }, [isLoading]);

  const handleRefresh = () => {
    setIsRefreshing(true);
    void refetchConversations();
    setTimeout(() => {
      setIsRefreshing(false);
    }, 500);
  };

  // Toolbar + search + list — shared between desktop's left panel and the
  // mobile single-pane layout. Defined here (not extracted to a component)
  // because every prop it needs is local state of InboxPageContent.
  const listPanelContent = (
    <>
      <section className="flex gap-2 items-center justify-between mb-4.5">
        <p className="truncate font-semibold text-foreground">{inboxTabTitle}</p>
        <div className="flex gap-2">
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    aria-label={t('bulk_send.button_label')}
                    className="focus-within:outline-none"
                    data-testid="new-message-button"
                  >
                    <Plus className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('bulk_send.button_label')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent>
              <DropdownMenuItem
                onClick={() => setShowBulkWhatsApp(true)}
                disabled={isWhatsAppUnavailable}
              >
                <div className="flex items-center gap-2 justify-between w-full">
                  <div className="flex items-center gap-2">
                    <WhatsappIcon
                      className={`size-4 ${
                        isWhatsAppUnavailable ? 'text-foreground-subtle' : 'text-green-500'
                      }`}
                    />
                    <span className={isWhatsAppUnavailable ? 'text-foreground-subtle' : ''}>
                      {t('bulk_send.whatsapp_option')}
                    </span>
                  </div>
                  {isWhatsAppUnavailable && (
                    <Badge variant="secondary" size="xs">
                      {t('bulk_send.no_whatsapp_integration')}
                    </Badge>
                  )}
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <div className="flex items-center gap-2 justify-between w-full">
                  <div className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4 text-foreground-subtle" />
                    <span className="text-foreground-subtle">{t('bulk_send.sms_option')}</span>
                  </div>
                  <Badge variant="secondary" size="xs">
                    {t('bulk_send.coming_soon')}
                  </Badge>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <div className="flex items-center gap-2 justify-between w-full">
                  <div className="flex items-center gap-2">
                    <TelegramIcon className="size-4 text-foreground-subtle" />
                    <span className="text-foreground-subtle">{t('bulk_send.telegram_option')}</span>
                  </div>
                  <Badge variant="secondary" size="xs">
                    {t('bulk_send.coming_soon')}
                  </Badge>
                </div>
              </DropdownMenuItem>
              <DropdownMenuItem disabled>
                <div className="flex items-center gap-2 justify-between w-full">
                  <div className="flex items-center gap-2">
                    <LinkedinIcon className="size-4 text-foreground-subtle" />
                    <span className="text-foreground-subtle">{t('bulk_send.linkedin_option')}</span>
                  </div>
                  <Badge variant="secondary" size="xs">
                    {t('bulk_send.coming_soon')}
                  </Badge>
                </div>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                onClick={handleRefresh}
                disabled={isLoading || isRefreshing}
                aria-label={t('tooltip.refresh')}
              >
                {isLoading || isRefreshing ? (
                  <Spinner className="h-4 w-4" />
                ) : (
                  <RefreshCw className="h-4 w-4" />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('tooltip.refresh')}</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="secondary"
                size="icon"
                onClick={() => setShowSearch((prev) => !prev)}
                aria-label={t('tooltip.search')}
              >
                <Search className="h-4 w-4" />
              </Button>
            </TooltipTrigger>
            <TooltipContent>{t('tooltip.search')}</TooltipContent>
          </Tooltip>
          <DropdownMenu>
            <Tooltip>
              <TooltipTrigger asChild>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="secondary"
                    size="icon"
                    aria-label={t('tooltip.more_actions')}
                    className="focus-within:outline-none"
                  >
                    <Settings className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
              </TooltipTrigger>
              <TooltipContent>{t('tooltip.more_actions')}</TooltipContent>
            </Tooltip>
            <DropdownMenuContent>
              <TranslationDropdown />
              <DropdownMenuSeparator />
              <VoiceAlertsDropdown />
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onClick={() => toggleModal(AUTO_CLOSE_SETTINGS_DIALOG)}
                data-testid="auto-close-settings-menu-item"
              >
                {t('setting_dropdown.auto_close_settings')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => toggleModal(CONVERSATION_BADGE_SETTINGS_DIALOG)}
                data-testid="conversation-badge-settings-menu-item"
              >
                {t('setting_dropdown.conversation_badge')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <Tooltip open={canExportConversations ? false : undefined}>
                <TooltipTrigger className="w-full">
                  <DropdownMenuItem
                    disabled={!canExportConversations}
                    onClick={() => toggleModal(exportAnalyticsDialogName(ExportMode.CONVERSATIONS))}
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      {t('setting_dropdown.export_conversation')}
                      {!canExportConversations && (
                        <UpgradeBadge
                          className="pointer-events-auto"
                          data-testid="export-conversations-upgrade-badge"
                        />
                      )}
                    </span>
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent className="z-[110]">
                  {t('setting_dropdown.export_conversation_disabled_tooltip')}
                </TooltipContent>
              </Tooltip>
              <Tooltip open={canExportDataGathering ? false : undefined}>
                <TooltipTrigger className="w-full">
                  <DropdownMenuItem
                    disabled={!canExportDataGathering}
                    onClick={() =>
                      toggleModal(exportAnalyticsDialogName(ExportMode.DATA_GATHERING))
                    }
                  >
                    {t('setting_dropdown.export_data')}
                  </DropdownMenuItem>
                </TooltipTrigger>
                <TooltipContent className="z-[110]">
                  {t('setting_dropdown.export_data_disabled_tooltip')}
                </TooltipContent>
              </Tooltip>
              <DropdownMenuItem
                onClick={() => toggleModal(exportAnalyticsDialogName(ExportMode.LINK_TRACKING))}
              >
                {t('setting_dropdown.export_link_tracking')}
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => toggleModal(exportAnalyticsDialogName(ExportMode.AI_INSIGHTS))}
              >
                {t('setting_dropdown.export_ai_insights')}
              </DropdownMenuItem>
              {TRIAGE_DATA_EXPORT_ALLOWED_SCENARIO_IDS.includes(SCENARIO_ID) && (
                <DropdownMenuItem
                  onClick={() => toggleModal(exportAnalyticsDialogName(ExportMode.TRIAGE_DATA))}
                >
                  {t('setting_dropdown.export_triage_data')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </section>
      {showSearch && (
        <ConversationSearch query={query} setQuery={setQuery} onSearchAgain={handleRefresh} />
      )}
      <ConversationList
        isLoading={isLoading || !isAnchorResolved || !isAboveFetched}
        conversations={conversations}
        loading={loading}
        hasNextPage={hasNextPage}
        sentryRef={sentryRef}
        isConversationPanelActive={isConversationPanelActive}
        conversationId={conversationId}
        anchorId={anchorId}
        anchorConversation={anchorConversation}
        aboveConversations={aboveConversations}
        hasMoreAbove={hasMoreAbove}
        onJumpToLatest={clearAnchor}
        error={error}
        onRetry={handleRefresh}
      />
    </>
  );

  return (
    <TooltipProvider>
      <div className="flex bg-background">
        {isMobile ? (
          <MobileInboxLayout
            listContent={listPanelContent}
            hasSelectedConversation={!!conversationId}
            listUrl={`/i/${SCENARIO_ID}/${IndexPath.inbox.root}${locationSearch}`}
            isFetched={isFetched}
          />
        ) : (
          <div className="flex flex-grow overflow-hidden h-[calc(100vh_-_53px)]">
            <ResizablePanelGroup direction="horizontal" autoSaveId="inbox-sidebars">
              <ResizablePanel defaultSize={panelSizes.defaultSize} minSize={panelSizes.minSideSize}>
                <div
                  className="flex h-full w-full flex-col border-r border-border bg-card p-4 pb-0"
                  onMouseDown={() => setIsConversationPanelActive(false)}
                >
                  <div className="flex flex-col h-full hide-scrollbar ">{listPanelContent}</div>
                </div>
              </ResizablePanel>

              <ResizableHandle withHandle className="bg-border" />

              <ResizablePanel minSize={40}>
                <div
                  className="flex h-full overflow-y-auto hide-scrollbar w-full"
                  onMouseDown={() => setIsConversationPanelActive(true)}
                >
                  {conversationId == null && !isFetched ? (
                    <div className="flex-1 flex items-center justify-center">
                      <Spinner className="h-7 w-7" />
                    </div>
                  ) : (
                    <Outlet />
                  )}
                </div>
              </ResizablePanel>

              <ResizableHandle withHandle className="bg-border" hidden={!conversationId} />

              <ResizablePanel
                ref={rightPanelRef}
                defaultSize={panelSizes.defaultSize}
                minSize={panelSizes.minSideSize}
                collapsible={true}
                collapsedSize={panelSizes.collapsedSize}
                onCollapse={() => setIsRightSidebarCollapsed(true)}
                onExpand={() => setIsRightSidebarCollapsed(false)}
              >
                <div
                  className="flex h-full w-full overflow-y-auto border-l border-border bg-card hide-scrollbar"
                  onMouseDown={() => setIsConversationPanelActive(true)}
                >
                  {conversationId && (
                    <ConversationDetails
                      isCollapsed={isRightSidebarCollapsed}
                      onCollapsedChange={handleToggleRightPanel}
                    />
                  )}
                </div>
              </ResizablePanel>
            </ResizablePanelGroup>
          </div>
        )}

        <ExportAnalyticsDialog exportMode={ExportMode.CONVERSATIONS} />
        <ExportAnalyticsDialog exportMode={ExportMode.DATA_GATHERING} />
        <ExportAnalyticsDialog exportMode={ExportMode.LINK_TRACKING} />
        <ExportAnalyticsDialog exportMode={ExportMode.AI_INSIGHTS} />
        {TRIAGE_DATA_EXPORT_ALLOWED_SCENARIO_IDS.includes(SCENARIO_ID) && (
          <ExportAnalyticsDialog exportMode={ExportMode.TRIAGE_DATA} />
        )}
        <AutoCloseSettingsDialog />
        <ConversationBadgeSettingsDialog />
        <BulkWhatsAppTemplateDialog open={showBulkWhatsApp} onOpenChange={setShowBulkWhatsApp} />
      </div>
    </TooltipProvider>
  );
};

export const InboxPage = () => (
  <ErrorBoundary FallbackComponent={ErrorFallback}>
    <InboxPageContent />
  </ErrorBoundary>
);
